import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { SEO_REDIRECTS } from "@/seo/redirects";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import CanonicalManager from "@/components/CanonicalManager";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import SeoHead from "@/components/SeoHead";
import PublicChrome from "@/components/PublicChrome";
import React, { lazy, Suspense } from "react";

// Eagerly loaded – landing page (LCP critical)
import Index from "./pages/Index";

// Lazy-loaded pages
const Signup = lazy(() => import("./pages/Signup"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const EmailVerified = lazy(() => import("./pages/EmailVerified"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));

const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const VdsBrandsLanding = lazy(() => import("./pages/VdsBrandsLanding"));
const PractitionersLanding = lazy(() => import("./pages/PractitionersLanding"));
const ComplianceQuiz = lazy(() => import("./pages/ComplianceQuiz"));
const CancellationFeedback = lazy(() => import("./pages/CancellationFeedback"));
const Anta = lazy(() => import("./pages/Anta"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));

// Dashboard layout + pages
const DashboardLayout = lazy(() => import("./components/dashboard/DashboardLayout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SubmitReview = lazy(() => import("./pages/dashboard/SubmitReview"));
const ClinicProfile = lazy(() => import("./pages/dashboard/ClinicProfile"));
const BrandProfile = lazy(() => import("./pages/dashboard/BrandProfile"));
const ProductProfiles = lazy(() => import("./pages/dashboard/ProductProfiles"));
const ProductProfileDetail = lazy(() => import("./pages/dashboard/ProductProfileDetail"));
const ReviewDetail = lazy(() => import("./pages/dashboard/ReviewDetail"));
const AuditVault = lazy(() => import("./pages/dashboard/AuditVault"));
const SettingsPage = lazy(() => import("./pages/dashboard/Settings"));
const Billing = lazy(() => import("./pages/dashboard/Billing"));
const Support = lazy(() => import("./pages/dashboard/Support"));
const Help = lazy(() => import("./pages/dashboard/Help"));

// Public layout + pages
const PublicLayout = lazy(() => import("./components/PublicLayout"));
const About = lazy(() => import("./pages/public/About"));
const Speaking = lazy(() => import("./pages/public/Speaking"));
const Terms = lazy(() => import("./pages/public/Terms"));
const Privacy = lazy(() => import("./pages/public/Privacy"));
const Dpa = lazy(() => import("./pages/public/Dpa"));
const PublicHelp = lazy(() => import("./pages/public/Help"));
const Contact = lazy(() => import("./pages/public/Contact"));
const Security = lazy(() => import("./pages/public/Security"));
const Insights = lazy(() => import("./pages/public/Insights"));
const InsightArticle = lazy(() => import("./pages/public/InsightArticle"));
const Guides = lazy(() => import("./pages/public/Guides"));
const GuideArticle = lazy(() => import("./pages/public/GuideArticle"));
const ComplianceIndex = lazy(() => import("./pages/public/ComplianceIndex"));
const Pricing = lazy(() => import("./pages/public/Pricing"));

const AIGovernance = lazy(() => import("./pages/public/AIGovernance"));
const ComplianceAnswer = lazy(() => import("./pages/public/ComplianceAnswer"));

const queryClient = new QueryClient();

export const AppProviders = ({ children }: { children: React.ReactNode }) => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>{children}</TooltipProvider>
  </QueryClientProvider>
);

export const AppRoutes = () => (
  <AuthProvider>
    <CanonicalManager />
    <SeoHead />
    <AnalyticsTracker />
    <Suspense fallback={null}>
            <Routes>
              {/* Redirects (no chrome needed) */}
              <Route path="/waitlist" element={<Navigate to="/signup" replace />} />
              <Route path="/faq" element={<Navigate to="/help" replace />} />
              <Route path="/checkout" element={<Navigate to="/signup" replace />} />
              <Route path="/success" element={<Navigate to="/dashboard?checkout=success" replace />} />

              {/* OAuth consent — must stay outside PublicChrome and dashboard guard */}
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

              {/* Public pages — shared dark gradient + ambient dot network */}
              <Route element={<PublicChrome />}>
                {/* Landing & auth pages */}
                <Route path="/" element={<Index />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/login" element={<Login />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/email-verified" element={<EmailVerified />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/vds-brands" element={<VdsBrandsLanding />} />
                <Route path="/practitioners" element={<PractitionersLanding />} />
                <Route path="/compliance-quiz" element={<ComplianceQuiz />} />
                <Route path="/cancellation-feedback" element={<CancellationFeedback />} />

                {/* Public pages with shared layout */}
                <Route element={<PublicLayout />}>
                  <Route path="/about" element={<About />} />
                  <Route path="/speaking" element={<Speaking />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/dpa" element={<Dpa />} />
                  <Route path="/help" element={<PublicHelp />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/security" element={<Security />} />
                  {Object.entries(SEO_REDIRECTS).map(([from, to]) => (
                    <Route key={from} path={from} element={<Navigate to={to} replace />} />
                  ))}
                  <Route path="/insights" element={<Insights />} />
                  <Route path="/insights/:slug" element={<InsightArticle />} />
                  <Route path="/guides" element={<Guides />} />
                  <Route path="/guides/:slug" element={<GuideArticle />} />
                  <Route path="/pricing" element={<Pricing />} />

                  <Route path="/ai-governance" element={<AIGovernance />} />
                  <Route path="/compliance" element={<ComplianceIndex />} />
                  <Route path="/compliance/:slug" element={<ComplianceAnswer />} />
                  <Route path="/anta" element={<Anta />} />
                </Route>
              </Route>


              {/* Dashboard (protected) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="review" element={<Navigate to="/dashboard/products" replace />} />
                <Route path="products" element={<ProductProfiles />} />
                <Route path="products/:id" element={<ProductProfileDetail />} />
                <Route path="reviews/:id" element={<ReviewDetail />} />
                <Route path="vault" element={<AuditVault />} />
                <Route path="clinic-profile" element={<ClinicProfile />} />
                <Route path="brand-profile" element={<BrandProfile />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="billing" element={<Navigate to="/dashboard/settings" replace />} />
                <Route path="support" element={<Support />} />
                <Route path="help" element={<Help />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
    </Suspense>
  </AuthProvider>
);

const App = () => (
  <AppProviders>
    <Toaster />
    <Sonner />
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </AppProviders>
);

export default App;
